import { SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";
import { CachePolicies } from "use-http";

import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { MetricsCounts } from "components/detector/DetectorStats/DetectorMetricsTable/types";
import { JANZU_URL } from "config/config";
import { METRIC_LIMIT_MAPPINGS, MetricLimitMapping, NO_QUERY_LIMIT } from "config/copies/detectorMetrics";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { fetchDetector, isFetchingPosicheck, setRequestFailed } from "store/slices/DetectorPage/detectorSlice";
import { Detector } from "types/detectors";


export interface Metrics {
  summary: {
    num_ground_truth: number;
    num_current_source_human: number;
    total_iqs: number;
    max_count?: number;
    class_names?: string[];
    unconfident_counts: {
      total: number;
    };
    ground_truth: {
      PASS: number;
      FAIL: number;
    };
    class_counts: {
      source_ml: Record<string, number>;
      source_human: MetricsCounts;
      cloud_labeler: MetricsCounts;
      cloud_ensemble: MetricsCounts;
      cloud: MetricsCounts;
      cust_labeler: MetricsCounts;
      total: MetricsCounts;
    };
    confidence_histogram: Array<HistogramEntry>;
  };
}

export type MetricClassCounts = Metrics["summary"]["class_counts"][keyof Metrics["summary"]["class_counts"]];

interface HistogramEntry {
  "ML Confident": number;
  Escalated: number;
  val?: number;
}

export enum AnswerType {
  FIRST_ANSWER = "system_answer",
  BEST_ANSWER = "current_best_answer",
}

const DEFAULT_METRICS_PERIOD = NO_QUERY_LIMIT;

interface TopLevelStatsProps {
  detectorData: Detector;
  count: number;
  confidenceThreshold: number;
}

export const useTopLevelStats = (props: TopLevelStatsProps) => {
  const { detectorData, count, confidenceThreshold } = props;
  const [histogramData, setHistogramData] = useState<HistogramEntry[]>([]);
  const [period, setPeriod] = useState<MetricLimitMapping>(DEFAULT_METRICS_PERIOD);
  const [answerType, setAnswerType] = useState<AnswerType>(AnswerType.BEST_ANSWER);
  const [loadingMetrics, setLoadingMetrics] = useState<Boolean>(false);
  const isPosicheckLoading = useAppSelector(isFetchingPosicheck);
  const dispatch = useAppDispatch();

  const metricsUrl = `${JANZU_URL}device-api/metrics/detectors/${detectorData.id}`;

  const {
    get: getMetrics,
    data: metrics,
    error: errorMetrics,
    loading: loadingGetMetrics,
  } = useFetchCustom<Metrics>(metricsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  useEffect(() => {
    getMetrics(`?type=summary${period.queryParams}&answer_type=${answerType}`);
    // eslint-disable-next-line
  }, []);

  // If an error occurs it will set the requestFailed state to true
  // which will stop re requests from happening
  useEffect(() => {
    if (errorMetrics) dispatch(setRequestFailed({ requestFailed: true }));
  }, [errorMetrics, dispatch]);

  // if not request is loading and the counter is higher than 10 will make a https request
  // so we get new metrics. also sets the state for the other component to know the http request state
  useEffect(() => {
    dispatch(fetchDetector({ isFetchingDetector: loadingGetMetrics }));
    if (!loadingGetMetrics && !isPosicheckLoading && count >= 10) {
      getMetrics(`?type=summary${period.queryParams}&answer_type=${answerType}`);
    }
    // eslint-disable-next-line
  }, [getMetrics, isPosicheckLoading, loadingGetMetrics, dispatch, count]);

  const handleActivityPeriodChange = async (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event;
    const selectedPeriod = METRIC_LIMIT_MAPPINGS.find((limitMapping) => limitMapping.ui === value);
    if (selectedPeriod) {
      setPeriod(selectedPeriod);
      setHistogramData([]);
      setLoadingMetrics(true);
      await getMetrics(`?type=summary${selectedPeriod.queryParams}&answer_type=${answerType}`);
      setLoadingMetrics(false);
    }
  };

  const handleAnswerTypeChange = async (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event;
    setAnswerType(value as AnswerType);
    setLoadingMetrics(true);
    await getMetrics(`?type=summary${period.queryParams}&answer_type=${value}`);
    setLoadingMetrics(false);
  };

  // once the data is loaded, set the metrics we'll be using
  useEffect(() => {
    if (metrics && metrics.summary) {
      if (metrics.summary.confidence_histogram) {
        const data = metrics.summary.confidence_histogram.map((entry, index) => ({ ...entry, val: index }));
        setHistogramData(data);
      }
    }
  }, [metrics, confidenceThreshold]);

  const status = SkeletonIndicator({ loading: !metrics, error: errorMetrics, height: 670 });

  return {
    answerType,
    handleActivityPeriodChange,
    handleAnswerTypeChange,
    histogramData,
    loadingMetrics,
    metrics,
    period,
    status,
  };
};
